import React from 'react'

import * as yup from 'yup'

import { Flex, Progress, Stack } from '@chakra-ui/react'
import {
  Typography,
  Button,
  DataGrid,
  Form,
  Spinner,
  Tab,
  Tag,
  TColumnDef,
  UploadFile,
  Modal,
  ModalContent,
  ModalBody,
  ModalHeader,
  ModalFooter,
  useModal,
} from '@pnld/components-web'

import { useQueryGetCensusYearSummary } from '@/api/prediction-processing/queries'
import { useMutationUploadFile } from '@/api/upload/mutations'
import { TUploadProgress } from '@/api/upload/types'
import {
  CensusFileError,
  countCensusSchools,
  readCensusFile,
  TCensusFileInfo,
} from '@/utils/censusFile'

import { useSendingsController } from './controller'
import {
  CENSUS_TABLES,
  formatCount,
  LoadingLine,
  StatusAlert,
  tablePosition,
} from './shared'

interface ISendDataModalProps {
  isOpen: boolean
  onClose: () => void
}

type TTableRow = {
  label: string
  current: number
  incoming: string
  isTotal?: boolean
}

// Contagem de escolas do arquivo, feita no navegador antes do envio.
type TSchoolCount =
  | { state: 'counting' }
  | { state: 'done'; value: number }
  | { state: 'error' }

const TABLE_LABELS: Record<string, string> = Object.fromEntries(
  CENSUS_TABLES.map(({ table, label }) => [table, label])
)

const cellText = (row: TTableRow, text: string) => (
  <Typography
    variant={row.isTotal ? 'h-2xs' : 'b-sm'}
    color="brand.neutral.dark_2"
  >
    {text}
  </Typography>
)

const tableColumns: Array<TColumnDef<TTableRow>> = [
  {
    accessorKey: 'label',
    header: 'Tabela',
    cell: ({ row }) => cellText(row.original, row.original.label),
  },
  {
    accessorKey: 'current',
    header: 'No banco hoje',
    cell: ({ row }) =>
      cellText(row.original, formatCount(row.original.current)),
  },
  {
    accessorKey: 'incoming',
    header: 'No arquivo',
    cell: ({ row }) => cellText(row.original, row.original.incoming),
  },
]

const isCounting = (count: TSchoolCount | null) =>
  !count || count.state === 'counting'

const schoolCountText = (count: TSchoolCount | null) => {
  if (!count || count.state === 'counting') return 'lendo o arquivo…'
  if (count.state === 'error') return 'não foi possível contar'
  return formatCount(count.value)
}

const FileSection = ({
  file,
  schoolCount,
}: {
  file: TCensusFileInfo
  schoolCount: TSchoolCount | null
}) => (
  <Stack gap={2}>
    <Typography variant="h-2xs" color="brand.neutral.dark_2">
      Arquivo selecionado: Censo {file.year}
    </Typography>
    <Flex align="center" gap={2}>
      <Typography variant="b-sm">
        Escolas no arquivo: {schoolCountText(schoolCount)}
      </Typography>
      {isCounting(schoolCount) && (
        <Spinner size="xs" color="brand.primary.dark_1" label="Carregando" />
      )}
    </Flex>
    <Flex gap={2} wrap="wrap" align="center">
      <Typography variant="b-sm">Importados:</Typography>
      {file.usedFiles.map(name => (
        <Tag key={name} variant="primary">
          {name}
        </Tag>
      ))}
    </Flex>
    {file.ignoredFiles.length > 0 && (
      <Flex gap={2} wrap="wrap" align="center">
        <Typography variant="b-sm">Ignorados:</Typography>
        {file.ignoredFiles.map(name => (
          <Tag key={name} variant="neutral">
            {name}
          </Tag>
        ))}
      </Flex>
    )}
  </Stack>
)

const formatMegabytes = (bytes: number) =>
  `${(bytes / 1024 / 1024).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} MB`

// Percentual arredondado para baixo: 100% só quando o último bloco chegou.
const UploadProgress = ({ progress }: { progress: TUploadProgress }) => {
  const percent =
    progress.bytesTotal > 0
      ? Math.floor((progress.bytesUploaded / progress.bytesTotal) * 100)
      : 0
  return (
    <Stack gap={1} mt={2}>
      <Typography variant="b-sm">
        {percent < 100
          ? `Enviando o arquivo: ${percent}% (${formatMegabytes(
              progress.bytesUploaded
            )} de ${formatMegabytes(progress.bytesTotal)})`
          : 'Concluindo o envio…'}
      </Typography>
      <Progress
        value={percent}
        aria-label="Progresso do envio"
        colorScheme="blue"
        hasStripe
        isAnimated
        borderRadius="full"
        size="sm"
      />
    </Stack>
  )
}

const SendDataModal = ({ isOpen, onClose }: ISendDataModalProps) => {
  const { formikRef, handleSubmit } = useSendingsController({ onClose })
  const confirmDialog = useModal()
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  const [censusFile, setCensusFile] = React.useState<TCensusFileInfo | null>(
    null
  )
  const [fileError, setFileError] = React.useState<string | null>(null)
  const [uploadError, setUploadError] = React.useState<string | null>(null)
  const [schoolCount, setSchoolCount] = React.useState<TSchoolCount | null>(
    null
  )
  const [uploadProgress, setUploadProgress] =
    React.useState<TUploadProgress | null>(null)
  // Arquivo da seleção atual: descarta resultado de uma seleção anterior.
  const currentFile = React.useRef<File | null>(null)
  const censusYear = censusFile?.year ?? null

  const {
    data: summary,
    isFetching: isCheckingYear,
    isError: isSummaryError,
  } = useQueryGetCensusYearSummary(censusYear)

  const tableRows: Array<TTableRow> = React.useMemo(() => {
    if (!summary) return []
    // As tabelas do censo têm uma linha por escola, exceto a de alunos por
    // etapa, que a UNPIVOT só calcula no import.
    const rows = Object.entries(summary.tables)
      .sort(([a], [b]) => tablePosition(a) - tablePosition(b))
      .map(([table, current]) => ({
        label: TABLE_LABELS[table] ?? table,
        current,
        incoming:
          table === 'tbCensoTsQtdAlunos'
            ? 'calculado no import'
            : schoolCountText(schoolCount),
      }))
    const total = rows.reduce((sum, row) => sum + row.current, 0)
    return [
      ...rows,
      { label: 'Total', current: total, incoming: '—', isTotal: true },
    ]
  }, [summary, schoolCount])
  const replacedRows = tableRows.at(-1)?.current ?? 0
  // O ano e o total de linhas já estão no título e no botão da confirmação.
  const dialogMessage =
    `Os dados atuais do Censo ${censusYear} serão trocados pelos do arquivo enviado` +
    (schoolCount?.state === 'done'
      ? `, com ${formatCount(schoolCount.value)} ${
          schoolCount.value === 1 ? 'escola' : 'escolas'
        }`
      : '') +
    '.'

  const { mutate: uploadFile, isLoading } = useMutationUploadFile({
    onSuccess: () => {
      handleSubmit({})
    },
    onError: error => {
      setUploadProgress(null)
      setUploadError(error.message)
    },
  })

  const resetSelection = () => {
    currentFile.current = null
    setSelectedFile(null)
    setCensusFile(null)
    setFileError(null)
    setUploadError(null)
    setSchoolCount(null)
    setUploadProgress(null)
  }

  // Fechar no meio do envio largava a sessão TUS pela metade (PSV-488).
  const handleClose = () => {
    if (isLoading) return
    resetSelection()
    onClose()
  }

  const handleDownloadMicrodata = () => {
    window.open(
      'https://www.gov.br/inep/pt-br/acesso-a-informacao/dados-abertos/microdados/censo-escolar',
      '_blank'
    )
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    resetSelection()
    currentFile.current = file
    setSelectedFile(file)
    readCensusFile(file)
      .then(info => {
        if (currentFile.current !== file) return
        setCensusFile(info)
        setSchoolCount({ state: 'counting' })
        countCensusSchools(file)
          .then(value => {
            if (currentFile.current === file)
              setSchoolCount({ state: 'done', value })
          })
          .catch(() => {
            if (currentFile.current === file) setSchoolCount({ state: 'error' })
          })
      })
      .catch(error =>
        setFileError(
          error instanceof CensusFileError
            ? error.message
            : 'Não foi possível ler o arquivo selecionado.'
        )
      )
  }

  const startUpload = (replaceConfirmed: boolean) => {
    if (!selectedFile || censusYear === null) return
    setUploadError(null)
    setUploadProgress({ bytesUploaded: 0, bytesTotal: selectedFile.size })
    uploadFile({
      file: selectedFile,
      censusYear,
      replaceConfirmed,
      onProgress: setUploadProgress,
    })
  }

  const handleSendClick = () => {
    if (!summary) return
    if (summary.hasData) {
      confirmDialog.onOpen()
      return
    }
    startUpload(false)
  }

  const handleConfirmReplace = () => {
    confirmDialog.onClose()
    startUpload(true)
  }

  // Entre a seleção e a prévia: a leitura do arquivo no navegador e a consulta
  // do ano no banco.
  const loadingText =
    selectedFile && !censusFile && !fileError
      ? 'Lendo o arquivo…'
      : censusYear !== null && !summary && !isSummaryError
      ? `Consultando os dados do Censo ${censusYear}…`
      : null

  const canSend =
    !!selectedFile &&
    censusYear !== null &&
    !!summary &&
    !isCheckingYear &&
    schoolCount?.state !== 'counting' &&
    !isLoading

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      // Título e botões ficam fixos; só o conteúdo rola.
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader noCloseButton={isLoading}>
          <Typography variant="h-xs" color="brand.neutral.dark_2">
            Enviar dados para base de predição
          </Typography>
        </ModalHeader>

        {/* Stack dentro do ModalBody: com `as`, o corpo perde a rolagem. */}
        <ModalBody>
          <Stack gap={4}>
            <Stack gap={1}>
              <Typography variant="b-sm">
                Para que a predição seja realizada, é necessário fazer o upload
                do arquivo completo de microdados do censo escolar, conforme
                disponibilizado pelo INEP: o ZIP oficial, sem extrair
                (obrigatório a partir de 2025), ou o CSV de 2023 e 2024.
              </Typography>
              <Stack direction="row">
                <Button
                  label="Baixar arquivo de microdados"
                  size="sm"
                  variant="secondary"
                  border="square"
                  onClick={handleDownloadMicrodata}
                />
              </Stack>
            </Stack>

            <Form
              innerRef={formikRef}
              onSubmit={handleSubmit}
              initialValues={{ fileUrl: '' }}
              validationSchema={yup.object().shape({
                fileUrl: yup.string().required('O arquivo é obrigatório'),
              })}
            >
              <UploadFile
                label="Selecione seu arquivo"
                name="fileUrl"
                accept=".csv,.zip"
                onChange={handleFileSelect}
                disabled={isLoading}
              />
              {isLoading && uploadProgress && (
                <UploadProgress progress={uploadProgress} />
              )}
            </Form>

            {loadingText && <LoadingLine>{loadingText}</LoadingLine>}
            {fileError && <StatusAlert status="error">{fileError}</StatusAlert>}
            {isSummaryError && (
              <StatusAlert status="error">
                Não foi possível consultar os dados do Censo {censusYear}. Tente
                selecionar o arquivo de novo.
              </StatusAlert>
            )}
            {summary && (
              <StatusAlert status={summary.hasData ? 'warning' : 'success'}>
                {summary.hasData
                  ? `O Censo ${
                      summary.nuCensusYear
                    } já está carregado. O envio substitui as ${formatCount(
                      replacedRows
                    )} linhas atuais.`
                  : `O Censo ${summary.nuCensusYear} ainda não tem dados carregados.`}
              </StatusAlert>
            )}
            {uploadError && (
              <StatusAlert status="error">{uploadError}</StatusAlert>
            )}

            {/* Ano novo não tem o que comparar: só a seção do arquivo. */}
            {censusFile &&
              summary &&
              (summary.hasData ? (
                <Tab
                  panels={[
                    {
                      title: 'Arquivo',
                      render: () => (
                        <FileSection
                          file={censusFile}
                          schoolCount={schoolCount}
                        />
                      ),
                    },
                    {
                      title: `Dados do Censo ${summary.nuCensusYear}`,
                      render: () => (
                        <DataGrid
                          columns={tableColumns}
                          data={tableRows}
                          hasPagination={false}
                          // Ordenar tiraria a linha de total do fim.
                          enableSorting={false}
                        />
                      ),
                    },
                  ]}
                />
              ) : (
                <FileSection file={censusFile} schoolCount={schoolCount} />
              ))}
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Button
            label="Cancelar"
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          />
          <Button
            label="Enviar dados"
            variant="primary"
            onClick={handleSendClick}
            disabled={!canSend}
          />
        </ModalFooter>
      </ModalContent>

      {/* Confirmação centralizada, por cima do modal de envio. */}
      <Modal
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        isCentered
      >
        <ModalContent maxW="560px">
          <ModalHeader>
            <Typography variant="h-xs" color="brand.neutral.dark_2">
              Substituir o Censo {censusYear}?
            </Typography>
          </ModalHeader>
          <ModalBody>
            <Stack gap={4}>
              <Typography variant="b-sm">{dialogMessage}</Typography>
              <StatusAlert status="warning">
                Esta ação não pode ser desfeita.
              </StatusAlert>
            </Stack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              label="Voltar"
              variant="secondary"
              onClick={confirmDialog.onClose}
            />
            <Button
              label={`Substituir o Censo ${censusYear} (${formatCount(
                replacedRows
              )} linhas)`}
              variant="primary"
              onClick={handleConfirmReplace}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Modal>
  )
}

export default SendDataModal
